import { X, ArrowSquareOut } from '@phosphor-icons/react';
import { formatLabel, ALERT_STATUS_STYLES, BADGE } from '@/lib/format';

/**
 * Shared read-only dialog that shows every known detail about a match alert,
 * including resolution / discounting comments and resolver metadata.
 * Used from Case Detail → Auto-Resolved, Resolved, and Weak Match tables.
 */
export default function MatchDetailDialog({ open, onClose, alert, caseData }) {
  if (!open || !alert) return null;
  const ed = alert.matched_entry_data || {};
  const layers = alert.match_layers || {};
  const aliases = Array.isArray(ed.aliases) ? ed.aliases : [];
  const score = typeof alert.match_score === 'number' ? alert.match_score : 0;
  const tier = score >= 85 ? 'Strong' : score >= 60 ? 'Potential' : 'Weak';
  const tierColor = score >= 85 ? 'text-red-600' : score >= 60 ? 'text-orange-600' : 'text-gray-600';
  const resolved = alert.status && alert.status !== 'pending';

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose} data-testid="match-detail-dialog">
      <div className="bg-white rounded-md border border-[#E4E4E7] shadow-lg w-full max-w-3xl max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E4E4E7] bg-[#F7F7F8]">
          <div className="flex items-center gap-3">
            <span className={`text-base font-bold ${tierColor}`}>{score.toFixed(1)}%</span>
            <span className={`${BADGE} ${score >= 85 ? 'bg-red-100 text-red-700 border-red-200' : score >= 60 ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>{tier}</span>
            <span className={ALERT_STATUS_STYLES[alert.status] || BADGE}>{formatLabel(alert.status || 'unknown')}</span>
            <span className="text-xs text-[#A1A1AA] uppercase">{alert.database || 'N/A'}</span>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#E4E4E7]" data-testid="match-detail-close"><X size={16} /></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-[#E4E4E7]">
          <div className="p-5">
            <p className="text-xs uppercase tracking-wider text-[#A1A1AA] font-medium mb-2">Case Entity</p>
            <p className="text-sm font-semibold mb-2">{caseData?.full_name || 'N/A'}</p>
            <div className="space-y-1 text-xs text-[#52525B]">
              {caseData?.case_type && <p>Type: {formatLabel(caseData.case_type)}</p>}
              {caseData?.date_of_birth && <p>DOB: {caseData.date_of_birth}</p>}
              {caseData?.nationality && <p>Nationality: {caseData.nationality}</p>}
              {caseData?.country_of_residence && <p>Residence: {caseData.country_of_residence}</p>}
              {caseData?.id_type && <p>ID: {caseData.id_type} {caseData.id_number || ''}</p>}
              {caseData?.occupation && <p>Occupation: {caseData.occupation}</p>}
            </div>
          </div>

          <div className="p-5">
            <p className="text-xs uppercase tracking-wider text-[#A1A1AA] font-medium mb-2">Matched Record</p>
            <p className="text-sm font-semibold mb-2">{alert.matched_entry_name || 'N/A'}</p>
            <div className="space-y-1 text-xs text-[#52525B]">
              {alert.matched_entry_source && <p><strong>Source:</strong> {alert.matched_entry_source}</p>}
              {ed.entity_type && <p><strong>Entity Type:</strong> {formatLabel(ed.entity_type)}</p>}
              {ed.nationality && <p><strong>Nationality:</strong> {ed.nationality}</p>}
              {ed.date_of_birth && <p><strong>DOB:</strong> {ed.date_of_birth}</p>}
              {ed.program && <p><strong>Programme:</strong> {ed.program}</p>}
              {ed.designation_date && <p><strong>Designated:</strong> {ed.designation_date}</p>}
              {ed.reason && <p><strong>Reason:</strong> {String(ed.reason).slice(0, 300)}</p>}
              {aliases.length > 0 && <p><strong>Aliases:</strong> {aliases.slice(0, 8).join(', ')}</p>}
              {ed.source_url && <a href={ed.source_url} target="_blank" rel="noopener noreferrer" className="text-[#0052FF] hover:underline flex items-center gap-1"><ArrowSquareOut size={12} /> View Original Source</a>}
            </div>
          </div>
        </div>

        {Object.keys(layers).length > 0 && (
          <div className="px-5 py-3 border-t border-[#E4E4E7]">
            <p className="text-xs uppercase tracking-wider text-[#A1A1AA] font-medium mb-2">Match Layers</p>
            <div className="flex gap-1.5 flex-wrap">
              {Object.entries(layers).map(([k, v]) => (
                <span key={k} className={`${BADGE} text-xs ${typeof v === 'number' && v > 50 ? 'bg-red-50 text-red-700 border-red-200' : typeof v === 'number' && v > 0 ? 'bg-yellow-50 text-yellow-700 border-yellow-200' : 'bg-gray-50 text-gray-400 border-gray-200'}`}>
                  {k}: {typeof v === 'number' ? v.toFixed(0) : 0}%
                </span>
              ))}
            </div>
          </div>
        )}

        {resolved && (
          <div className="px-5 py-4 border-t border-[#E4E4E7] bg-[#F7F7F8]">
            <p className="text-xs uppercase tracking-wider text-[#A1A1AA] font-medium mb-2">Resolution / Discounting</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-2 text-xs">
              <div><span className="text-[#A1A1AA]">Status</span><p className="font-medium">{formatLabel(alert.status)}</p></div>
              {alert.resolved_by && <div><span className="text-[#A1A1AA]">Resolved By</span><p className="font-medium">{alert.resolved_by}</p></div>}
              {alert.resolved_at && <div><span className="text-[#A1A1AA]">Resolved At</span><p className="font-medium">{new Date(alert.resolved_at).toLocaleString()}</p></div>}
            </div>
            {alert.resolution_comment ? (
              <div>
                <span className="text-[#A1A1AA] text-xs">Analyst Remark</span>
                <p className="mt-1 text-sm text-[#0A0A0A] whitespace-pre-wrap bg-white border border-[#E4E4E7] rounded-md p-3">{alert.resolution_comment}</p>
              </div>
            ) : (
              <p className="text-xs text-[#A1A1AA] italic">No remark recorded.</p>
            )}
          </div>
        )}

        <div className="px-5 py-3 border-t border-[#E4E4E7] flex items-center justify-between text-xs text-[#A1A1AA]">
          <span>Alert ID: {alert.alert_id || alert.id || '—'}</span>
          <span>Created: {alert.created_at ? new Date(alert.created_at).toLocaleString() : '—'}</span>
        </div>
      </div>
    </div>
  );
}
